import { useEffect, useMemo } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { EXPERIENCE_TYPEAHEAD_SEARCH_TYPES } from 'constants/experience'
import { useAppSelector } from 'hooks/reduxHooks'
import { useTypeaheadStreaming } from 'hooks/Search/useTypeaheadStreaming'
import { useLocalizedSearchItems } from 'hooks/usePlace'
import { EmptyArray, take } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { prependCanonicalExperienceDestination } from 'lib/search/canonicalExperienceDestination'
import {
  createExperienceNaturalLanguageItem,
  isExperienceNaturalLanguageItem,
} from 'lib/search/experienceNaturalLanguageSearch'
import { getSearchItemImageId } from 'lib/search/searchItem'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import SearchMenuListItem from '../SearchForm/SearchMenu/SearchMenuListItem'
import usePlaceSelectResultsChange from '../SearchPlaceSelect/usePlaceSelectResultsChange'
import SearchTextListItem from '../SearchTextListItem/SearchTextListItem'

const EXPERIENCE_VERTICAL: App.SearchPlaceVertical = 'experience'
/** Maximum number of streamed destination/experience matches shown below the typed query. */
const MATCH_LIMIT = 9

const ResultContainer = styled.div`
  position: relative;
  min-height: ${rem(80)};
`

const MatchContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: ${rem(40)};
`

interface Props {
  className?: string
  searchPhrase: string
  /** Parent-owned typeahead ID, shared by the stream, the submit ref and the typed-query row */
  typeaheadId: string
  selectedDropdown?: number
  onChange?: (searchItem?: App.SearchItem) => void
  onResultsChange?: (results: Array<App.SearchItem>, searchPhrase: string, shouldTrack?: boolean, loading?: boolean) => void
  /** Receives the stream's debounce controls so selection and submission can cancel or flush pending requests */
  temporarySearchSubmitRef?: React.MutableRefObject<App.TypeaheadSubmitRefData | null>
}

/**
 * Experience natural-language suggestions: the typed query first, followed by streamed
 * destination and experience matches. Streaming only — an empty, pending or failed stream
 * leaves just the typed query rather than falling back to HTTP typeahead.
 */
function ExperiencesSearchPlaceSelectList(props: Props) {
  const {
    className,
    searchPhrase,
    typeaheadId,
    selectedDropdown,
    onChange = noop,
    onResultsChange = noop,
    temporarySearchSubmitRef,
  } = props

  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const trimmedSearchPhrase = searchPhrase.trim()

  const { dropdownData, cancelDebounce, flushDebounce } = useTypeaheadStreaming(searchPhrase, typeaheadId, {
    types: EXPERIENCE_TYPEAHEAD_SEARCH_TYPES,
    limit: MATCH_LIMIT,
    isLuxPlusMember: canViewLuxPlusBenefits,
    inputThreshold: 1,
  })

  // Runs on every keystroke so the controls attach to the ref object the parent
  // (re)creates synchronously while typing.
  useEffect(() => {
    if (temporarySearchSubmitRef?.current) {
      temporarySearchSubmitRef.current.cancelDebounce = cancelDebounce
      temporarySearchSubmitRef.current.flushDebounce = flushDebounce
    }
  }, [temporarySearchSubmitRef, cancelDebounce, flushDebounce, searchPhrase])

  // No stream data yet for this ID. A failed stream still yields data (with `streamingError`), so it ends loading.
  // Keyboard handling only lets Enter pick a match once loading has finished.
  const loading = dropdownData === undefined

  const typedQueryItem = useMemo(() => {
    if (!trimmedSearchPhrase) return undefined
    return createExperienceNaturalLanguageItem(trimmedSearchPhrase, typeaheadId)
  }, [trimmedSearchPhrase, typeaheadId])

  const unlocalizedMatches = useMemo<Array<App.SearchItem>>(() => {
    if (!trimmedSearchPhrase) return EmptyArray
    const streamedMatches = (dropdownData?.results ?? EmptyArray).filter(result => !isExperienceNaturalLanguageItem(result))
    return take(prependCanonicalExperienceDestination(streamedMatches, searchPhrase, EXPERIENCE_VERTICAL), MATCH_LIMIT)
  }, [dropdownData?.results, searchPhrase, trimmedSearchPhrase])
  const matches = useLocalizedSearchItems(unlocalizedMatches)

  // The typed query is always the first selectable row, so keyboard indices line up with what is rendered.
  const results = useMemo<Array<App.SearchItem>>(
    () => typedQueryItem ? [typedQueryItem, ...matches] : matches,
    [typedQueryItem, matches],
  )

  usePlaceSelectResultsChange(results, searchPhrase, loading, onResultsChange)

  const matchIndexOffset = typedQueryItem ? 1 : 0

  return <ResultContainer className={className}>
    <VerticalSpacer gap={24}>
      {typedQueryItem && <SearchTextListItem
        searchItem={typedQueryItem}
        isHighlighted={selectedDropdown === undefined || selectedDropdown === 0}
        onClick={onChange}
      />}
      {matches.length > 0 && <MatchContainer>
        {matches.map((searchItem, index) => <SearchMenuListItem
          key={`${searchItem.value}_${index}`}
          searchItem={searchItem}
          onClick={onChange}
          searchPhrase={searchPhrase}
          selected={index + matchIndexOffset === selectedDropdown}
          searchVertical={EXPERIENCE_VERTICAL}
          imageId={getSearchItemImageId(searchItem)}
          horizontal
          useImage
        />)}
      </MatchContainer>}
    </VerticalSpacer>
  </ResultContainer>
}

export default ExperiencesSearchPlaceSelectList
